import type { IconsType } from './HXCIcon.types';
import { icons } from './iconMap';
import { SvgIcon, SxProps, Theme } from '@mui/material';
import {
  type HealthxchangeThemeColours,
  HealthxchangeThemeColours as HCXColor,
} from '../../theme/defaultTheme';
import { useMemo } from 'react';

export interface HXCIconProps {
  name: IconsType;
  testId?: string;
  color?: keyof typeof HealthxchangeThemeColours;
  scale?: number;
  // When provided, takes precedence over scale and sets exact pixel size
  sizePx?: number;
  sx?: SxProps<Theme>;
}

const HXCIcon: React.FC<HXCIconProps> = ({ name, testId, color, scale = 1, sizePx, sx }) => {
  const IconComponent = icons[name] as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

  const { viewBoxSize, widthEm, heightEm, isRenderable } = useMemo(() => {
    const fallback = { viewBoxSize: 24, widthEm: '1em', heightEm: '1em', isRenderable: false };

    // Guard: return defaults if icon not found or not a function
    if (!IconComponent || typeof IconComponent !== 'function') {
      return fallback;
    }

    const iconElement = IconComponent({}) as React.ReactElement | null;
    if (!iconElement || !iconElement.props) {
      return fallback;
    }

    const width = Number(iconElement?.props?.width ?? 24);
    const height = Number(iconElement?.props?.height ?? 24);

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return fallback;
    }

    const maxDimension = Math.max(width, height) || 24;

    // Calculate width and height in em units relative to the largest dimension
    // This way fontSize controls the largest dimension, and width/height maintain aspect ratio
    return {
      viewBoxSize: maxDimension,
      widthEm: `${width / maxDimension}em`,
      heightEm: `${height / maxDimension}em`,
      isRenderable: true,
    };
  }, [IconComponent]);

  // Guard clause: return null if icon not found or not a function (e.g., in tests)
  if (!IconComponent || typeof IconComponent !== 'function' || !isRenderable) {
    return null;
  }

  return (
    <SvgIcon
      component={IconComponent}
      inheritViewBox
      data-testid={testId}
      htmlColor={HCXColor[color || 'black']}
      fill={'none'}
      sx={{
        ...sx,
        fontSize: sizePx ?? viewBoxSize * scale,
        width: widthEm,
        height: heightEm,
        '&.MuiSvgIcon-root': {
          fill: 'none',
        },
      }}
    />
  );
};

export default HXCIcon;
